import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import WorkbookVideoPlayer from './WorkbookVideoPlayer';
import { checkAndUnlockPostAssessment, WorkbookCompletionModal } from '../utils/checkAndUnlockPostAssessment';

const AI_SCORING_PROMPT = `You are an expert AI agent design coach evaluating a learner's agent candidate analysis.

Score each criterion 1-4 (1=Missing, 2=Partial, 3=Solid, 4=Excellent).

CANDIDATE FIT: Does this process genuinely meet all five good agent criteria? (1-4)

TRIGGER PRECISION: Is the trigger specific enough to configure a real agent? (1-4)

INSTRUCTION QUALITY: Are the instructions complete, step-by-step, with no skipped steps? (1-4)

TOOLS IDENTIFIED: Are all necessary tools named with the type of access required? (1-4)

GUARDRAILS: Are there specific guardrails including an escalation condition? (1-4)

After scoring:
1. Give a total score out of 20.
2. Identify the single weakest ingredient and rewrite it to show what excellent looks like.
3. Is there a step in the instructions that a human should review before the agent proceeds? If yes, name it.
4. Name one edge case where the trigger might fire but the instructions would produce the wrong result.

Here is my agent candidate analysis:

[PASTE YOUR PROCESS NAME, FIVE CRITERIA CHECK, AND ALL FOUR INGREDIENTS]`;

const RUBRIC_ROWS = [
  { criterion: 'CANDIDATE FIT', missing: "Process doesn't meet agent criteria", partial: '1-2 criteria met', solid: '3-4 criteria met', excellent: 'All 5 criteria clearly met with honest reasoning' },
  { criterion: 'TRIGGER PRECISION', missing: 'No trigger or vague description', partial: 'General trigger category', solid: 'Specific condition named', excellent: 'Exact condition that could configure a real agent' },
  { criterion: 'INSTRUCTION QUALITY', missing: 'No instructions or just the goal', partial: 'Instructions exist but skipped steps', solid: 'All major steps present', excellent: 'Step-by-step, complete, could configure an agent right now' },
  { criterion: 'TOOLS IDENTIFIED', missing: 'No tools named', partial: 'One tool named', solid: 'All necessary tools identified', excellent: 'Tools named with read/write/send access type specified' },
  { criterion: 'GUARDRAILS', missing: 'No guardrails', partial: 'One vague guardrail', solid: 'Meaningful boundary condition', excellent: 'Specific guardrails with escalation logic included' },
];

const SCENARIOS = [
  { key: 's1', label: 'Scenario 1', text: 'A customer submits a support ticket. The agent responds immediately with the three most relevant FAQ answers and asks if any of them resolve the issue.' },
  { key: 's2', label: 'Scenario 2', text: 'A new contract is uploaded to SharePoint. The agent extracts the key dates and payment terms, then drops a structured summary into the deals tracker.' },
  { key: 's3', label: 'Scenario 3', text: 'A project goes more than 3 days without a status update. The agent sends a flag to the project manager with a list of overdue items.' },
  { key: 's4', label: 'Scenario 4', text: 'An employee asks "How many days of PTO do I have left?" in the HR Teams channel.' },
];

const CRITERIA = [
  'Repetitive and rule-based — same trigger, same response, every time',
  'High volume — runs frequently enough to justify the setup',
  'Low stakes per run — a mistake is recoverable',
  'Clear success criteria — I can tell if it ran correctly',
  'No judgment required — the right action is predictable from the inputs',
];

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, minHeight = 60 }) {
  return (
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      <textarea style={{ ...s.textarea, minHeight }} value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function ScoreField({ label, value, onChange }) {
  return (
    <div style={s.scoreField}>
      <div style={s.scoreLabel}>{label}</div>
      <select style={s.scoreSelect} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
      </select>
      <div style={s.scoreOf}>/ 4</div>
    </div>
  );
}

function RubricTable({ rows }) {
  return (
    <div style={s.rubricTable}>
      <div style={s.rubricHeader}>
        {['CRITERION', '1 — MISSING', '2 — PARTIAL', '3 — SOLID', '4 — EXCELLENT'].map(h => (
          <div key={h} style={s.rubricCol0}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={s.rubricRow}>
          <div style={{ ...s.rubricCol0, background: '#F2F0EC', color: '#1B2A4A', fontWeight: '700' }}>{row.criterion}</div>
          <div style={s.rubricCol}>{row.missing}</div>
          <div style={s.rubricCol}>{row.partial}</div>
          <div style={s.rubricCol}>{row.solid}</div>
          <div style={s.rubricCol}>{row.excellent}</div>
        </div>
      ))}
    </div>
  );
}

function CalloutBox({ title, children }) {
  return (
    <div style={s.callout}>
      <div style={s.calloutTitle}>{title}</div>
      <div style={s.calloutBody}>{children}</div>
    </div>
  );
}

function calcTotal(data, keys) {
  return keys.reduce((sum, k) => sum + (parseInt(data[k]) || 0), 0);
}

export default function WorkbookDay6({ user, onNav }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPostUnlockModal, setShowPostUnlockModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      const wbSnap = await getDoc(doc(db, 'workbooks', user.uid));
      if (wbSnap.exists()) {
        const prog = wbSnap.data().progress || {};
        const d = prog['day6'] || {};
        setData(d.sections || {});
        setIsComplete(d.status === 'complete');
      }
    };
    load();
  }, [user.uid]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day6: { ...(existing.progress?.day6 || {}), sections: data, updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setSaving(false);
    setLastSaved(new Date());
  };

  const handleMarkComplete = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day6: { ...(existing.progress?.day6 || {}), sections: data, status: 'complete', completedAt: serverTimestamp(), updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setIsComplete(true);
    setShowCompleteModal(false);
    setSaving(false);
    // Check if all assigned workbooks are now complete, unlock post-assessment if so
    const unlockResult = await checkAndUnlockPostAssessment(user.uid);
    if (unlockResult.unlocked) {
      setShowPostUnlockModal(true);
    }
  };

  const handleUnmarkComplete = async () => {
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      progress: { ...(existing.progress || {}), day6: { ...(existing.progress?.day6 || {}), status: 'incomplete', updatedAt: serverTimestamp() } }
    }, { merge: true });
    setIsComplete(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(AI_SCORING_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const rubricTotal = calcTotal(data, ['score_fit', 'score_trigger', 'score_instructions', 'score_tools', 'score_guardrails']);

  return (
    <div style={s.wrapper}>
      <div style={s.topBar}>
        <span style={s.backLink} onClick={() => onNav('workbooks')}>← Back to Workbooks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isComplete && <div style={s.completeBadge}>✓ Day 6 Complete</div>}
          {lastSaved && <span style={s.savedNote}>Saved {lastSaved.toLocaleTimeString()}</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      <WorkbookVideoPlayer dayNumber={6} user={user} />

      <div style={s.header}>
        <div style={s.dayTag}>Day 6</div>
        <div style={s.headerTitle}>Agent Basics</div>
        <div style={s.headerDesc}>Prompts are things you run. Agents are things that run for you.</div>
      </div>

      <div style={s.keyConceptBox}>
        <div style={s.keyConceptLabel}>Key Concept Reminder</div>
        <div style={s.keyConceptText}>
          Prompts are things you run. Agents are things that run on your behalf. An AI agent watches for a specific trigger condition and, when it fires, carries out a defined sequence of steps without you starting it. Every agent needs four things: a trigger (what starts it), instructions (what it does, step by step), tools (what it has access to), and guardrails (what it must not do). Three agent types: notification (watches and alerts you), action (watches and executes), and conversational (responds to human questions in real time). Good agent candidates are repetitive, rule-based, high-volume, low-stakes, and require no judgment. Keep it human when the work requires empathy, nuance, relationship, or when the person on the receiving end needs to know a human decided.
        </div>
      </div>

      <Section title="Part 1 — Agent Type Recognition">
        <div style={s.exerciseIntro}>For each scenario below, identify which agent type fits best and explain why in one sentence.</div>

        {SCENARIOS.map((sc, i) => (
          <div key={sc.key} style={{ ...s.scenBlock, marginBottom: i < SCENARIOS.length - 1 ? '14px' : '0' }}>
            <div style={s.scenHeader}>{sc.label}</div>
            <div style={s.scenText}>{sc.text}</div>
            <div style={s.scenTypeRow}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#1B2A4A' }}>Agent type:</span>
              {['Notification', 'Action', 'Conversational'].map(opt => (
                <label key={opt} style={s.radioLabel}>
                  <input type="radio" name={sc.key} checked={data[`${sc.key}_type`] === opt} onChange={() => set(`${sc.key}_type`, opt)} />
                  <span style={{ fontSize: '13px', color: '#3D3D3D' }}>{opt}</span>
                </label>
              ))}
            </div>
            <div style={s.scenField}>
              <div style={s.fieldLabel}>Why:</div>
              <textarea style={{ ...s.textarea, minHeight: 50 }} value={data[`${sc.key}_why`] || ''} onChange={e => set(`${sc.key}_why`, e.target.value)} />
            </div>
          </div>
        ))}
      </Section>

      <Section title="Part 2 — Find Your Agent Candidate">
        <div style={s.exerciseIntro}>Look at your Day 5 redesigned workflow, your Prompt Library, and your use case list, and if you ran the AI Use Case Finder back in Day 3, pull up the plays it flagged as worth building an agent for, because those were pointed at exactly this moment. Find one step or process that runs on the same trigger every time.</div>

        <div style={s.partHeader}>STEP 1 — CHECK AGAINST THE FIVE CRITERIA</div>
        <Field label="My candidate process:" value={data.candidate} onChange={v => set('candidate', v)} minHeight={44} />

        <div style={s.critTable}>
          <div style={s.critHeader}>
            <div style={{ ...s.critHCol, flex: 1 }}>Criterion</div>
            <div style={{ ...s.critHCol, width: '130px', flexShrink: 0 }}>Pass / Fail</div>
            <div style={{ ...s.critHCol, flex: 1, borderRight: 'none' }}>Notes</div>
          </div>
          {CRITERIA.map((crit, i) => (
            <div key={i} style={{ ...s.critRow, borderBottom: '1px solid #E8E6E2' }}>
              <div style={{ ...s.critCell, flex: 1 }}>{crit}</div>
              <div style={{ ...s.critCheckCell, width: '130px', flexShrink: 0 }}>
                <label style={s.radioLabel}><input type="radio" name={`crit_${i}`} checked={data[`crit_${i}`] === 'Pass'} onChange={() => set(`crit_${i}`, 'Pass')} /><span style={{ fontSize: '12px', color: '#3D3D3D' }}>Pass</span></label>
                <label style={s.radioLabel}><input type="radio" name={`crit_${i}`} checked={data[`crit_${i}`] === 'Fail'} onChange={() => set(`crit_${i}`, 'Fail')} /><span style={{ fontSize: '12px', color: '#3D3D3D' }}>Fail</span></label>
              </div>
              <div style={{ ...s.critNotesCell, flex: 1 }}>
                <textarea style={s.critNotesInput} value={data[`crit_${i}_notes`] || ''} onChange={e => set(`crit_${i}_notes`, e.target.value)} />
              </div>
            </div>
          ))}
          <div style={{ ...s.critRow, background: '#F2F0EC', borderBottom: 'none' }}>
            <div style={{ ...s.critCell, flex: 1, fontWeight: '700', color: '#1B2A4A' }}>Verdict</div>
            <div style={{ gridColumn: 'span 2', flex: 2, display: 'flex', alignItems: 'center', gap: '16px', padding: '10px 12px', flexWrap: 'wrap', borderLeft: '1px solid #E8E6E2' }}>
              <label style={s.radioLabel}><input type="radio" name="verdict" checked={data.verdict === 'candidate'} onChange={() => set('verdict', 'candidate')} /><span style={{ fontSize: '13px', color: '#3D3D3D' }}>Good agent candidate</span></label>
              <label style={s.radioLabel}><input type="radio" name="verdict" checked={data.verdict === 'human'} onChange={() => set('verdict', 'human')} /><span style={{ fontSize: '13px', color: '#3D3D3D' }}>Keep it human — reason:</span></label>
              {data.verdict === 'human' && (
                <textarea style={{ ...s.textarea, flex: 1, minWidth: '160px', minHeight: 36 }} value={data.verdict_reason || ''} onChange={e => set('verdict_reason', e.target.value)} />
              )}
            </div>
          </div>
        </div>

        <div style={{ ...s.partHeader, marginTop: '20px' }}>STEP 2 — NAME THE FOUR INGREDIENTS</div>
        <div style={{ fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '14px' }}>
          If your candidate passed, fill in all four ingredients. This is your Day 7 build brief.
        </div>

        <div style={s.ingredBlock}>
          <div style={s.ingredTitle}>Agent Brief</div>
          {[
            { num: '01', name: 'TRIGGER', key: 'ingred_trigger', hint: 'What specific condition starts this agent? Be precise: vague triggers produce unpredictable agents. Example: A new row is added to the project tracker with status "Blocked"', tall: 80 },
            { num: '02', name: 'INSTRUCTIONS', key: 'ingred_instructions', hint: 'What does the agent do when the trigger fires? Write step by step. Numbered. No skipped steps.', tall: 110 },
            { num: '03', name: 'TOOLS', key: 'ingred_tools', hint: 'What does the agent need access to? Example: Project tracker (read), Slack (send message), Status log (write)', tall: 80 },
            { num: '04', name: 'GUARDRAILS', key: 'ingred_guardrails', hint: "What must the agent NOT do? What should trigger an escalation to a human? Example: Don't send to anyone outside the immediate project team. Escalate if the blocker has been open more than 5 days.", tall: 80 },
          ].map((row, i, arr) => (
            <div key={row.key} style={{ ...s.ingredRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.ingredLabel}>
                <div style={s.ingredNum}>{row.num}</div>
                <div style={s.ingredName}>{row.name}</div>
              </div>
              <div style={s.ingredHint}>{row.hint}</div>
              <textarea
                style={{ ...s.textarea, flex: 1, border: 'none', minHeight: row.tall, background: '#FAF8F5' }}
                value={data[row.key] || ''}
                onChange={e => set(row.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Scoring Criteria">
        <div style={{ fontSize: '13px', color: '#3D3D3D', marginBottom: '16px', lineHeight: '1.6' }}>
          Use these criteria to evaluate your agent candidate analysis.
        </div>
        <RubricTable rows={RUBRIC_ROWS} />
        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1B2A4A', margin: '16px 0 12px' }}>Score your agent candidate analysis:</div>
        <div style={s.scoreRow}>
          <ScoreField label="Fit" value={data.score_fit} onChange={v => set('score_fit', v)} />
          <ScoreField label="Trigger" value={data.score_trigger} onChange={v => set('score_trigger', v)} />
          <ScoreField label="Instructions" value={data.score_instructions} onChange={v => set('score_instructions', v)} />
          <ScoreField label="Tools" value={data.score_tools} onChange={v => set('score_tools', v)} />
          <ScoreField label="Guardrails" value={data.score_guardrails} onChange={v => set('score_guardrails', v)} />
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Total</div>
            <div style={s.scoreTotalVal}>{rubricTotal} / 20</div>
          </div>
        </div>
      </Section>

      <div style={s.aiBox}>
        <div style={s.aiBoxTitle}>AI Scoring Prompt — Evaluate Your Own Work</div>
        <div style={s.aiBoxSub}>Paste this prompt along with your complete agent candidate analysis, all four ingredients filled in.</div>
        <div style={s.aiBoxText}>{AI_SCORING_PROMPT}</div>
        <button style={s.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy AI Scoring Prompt'}</button>
      </div>

      <Section title="Scores from AI Evaluation">
        <Field label="AI Score: ___ / 20     Weakest ingredient rewritten — what changed?" value={data.ai_score} onChange={v => set('ai_score', v)} minHeight={66} />
        <Field label="Edge case the AI identified — how would you handle it?" value={data.ai_edge} onChange={v => set('ai_edge', v)} minHeight={66} />
      </Section>

      <CalloutBox title="THE STANDARD: 16 / 20">
        Keep working until your agent candidate analysis scores 16/20 or higher AND all four ingredients are specific enough that someone could build this agent from your description tomorrow. If the trigger or instructions are still vague, revise them now.
      </CalloutBox>

      <Section title="Revision Workspace">
        <div style={s.revisionLabel}>REVISION 1</div>
        <Field label="What I changed and why:" value={data.rev1_what} onChange={v => set('rev1_what', v)} />
        <Field label="Revised ingredient (write the full updated version):" value={data.rev1_ingred} onChange={v => set('rev1_ingred', v)} minHeight={80} />
        <Field label="New AI Score: ___ / 20" value={data.rev1_score} onChange={v => set('rev1_score', v)} minHeight={44} />
      </Section>

      <Section title="Reflection">
        <Field
          label="1. When you applied the five criteria to your candidate, which one was the hardest to honestly answer? What does that tell you about the process?"
          value={data.reflection_1}
          onChange={v => set('reflection_1', v)}
          minHeight={80}
        />
        <Field
          label="2. Think about a colleague or team member whose work is most repetitive and rule-based. Without naming them, describe the kind of work they do. Is there an agent that could run on their behalf? What would change for them?"
          value={data.reflection_2}
          onChange={v => set('reflection_2', v)}
          minHeight={80}
        />
      </Section>

      <Section title="Wins Tracker — Day 6">
        <div style={s.winsTable}>
          <div style={s.winsHeader}>
            {['What I Tried', 'Tool Used', 'What Changed', 'Time Saved'].map(h => (
              <div key={h} style={s.winsCol}>{h}</div>
            ))}
          </div>
          {[1, 2, 3].map(n => (
            <div key={n} style={s.winsRow}>
              {['tried', 'tool', 'changed', 'saved'].map(f => (
                <div key={f} style={s.winsCell}>
                  <textarea style={s.winsInput} value={data[`wins_${n}_${f}`] || ''} onChange={e => set(`wins_${n}_${f}`, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <CalloutBox title="BEFORE DAY 7 — YOUR ASSIGNMENT">
        <span>Finalize your agent candidate brief. You're bringing this document to Day 7 to build from:</span>
        <ol style={{ paddingLeft: '18px', marginTop: '8px', lineHeight: '1.8' }}>
          <li>Write the final trigger condition: exact, precise, specific enough to configure.</li>
          <li>Write the final step-by-step instructions: numbered, complete, no skipped steps.</li>
          <li>List every tool the agent needs with the type of access required (read, write, send).</li>
          <li>Write the guardrails: at least two specific constraints and one escalation condition.</li>
          <li>Run the AI scoring prompt one more time on your finalized version. Aim for 18+/20 before Day 7.</li>
        </ol>
      </CalloutBox>
      <Field label="My final AI score on the agent brief: ___ / 20" value={data.final_score} onChange={v => set('final_score', v)} minHeight={44} />

      <div style={s.bottomBar}>
        <div style={s.saveRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Progress'}</button>
          {lastSaved && <span style={s.savedNote}>Last saved {lastSaved.toLocaleTimeString()}</span>}
        </div>
        {isComplete ? (
          <div style={s.completeRow}>
            <div style={s.completedBadge}>✓ Day 6 Complete</div>
            <button style={s.unmarkBtn} onClick={handleUnmarkComplete}>Unmark as Complete</button>
          </div>
        ) : (
          <button style={s.completeBtn} onClick={() => setShowCompleteModal(true)}>Mark Day 6 as Complete</button>
        )}
      </div>

      {showCompleteModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark Day 6 as Complete?</div>
            <div style={s.modalText}>This will count toward your certificate progress. You can unmark it if you need to come back.</div>
            <div style={s.modalBtns}>
              <button style={s.modalCancel} onClick={() => setShowCompleteModal(false)}>Cancel</button>
              <button style={s.modalConfirm} onClick={handleMarkComplete}>Yes, Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      <WorkbookCompletionModal
        isOpen={showPostUnlockModal}
        onClose={() => setShowPostUnlockModal(false)}
        onGoToAssessment={() => { setShowPostUnlockModal(false); onNav('assessment'); }}
        onGoToExam={() => { setShowPostUnlockModal(false); onNav('exam'); }}
      />
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '720px', paddingBottom: '60px' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '12px 20px', marginBottom: '16px', position: 'sticky', top: '52px', zIndex: 50 },
  backLink: { fontSize: '12px', color: '#1B2A4A', cursor: 'pointer', fontWeight: '500' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  headerTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '6px' },
  headerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '300' },
  completeBadge: { display: 'inline-block', background: '#1a8a4a', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px' },
  keyConceptBox: { background: '#F2F0EC', borderLeft: '3px solid #D4A847', padding: '16px 20px', marginBottom: '20px' },
  keyConceptLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  keyConceptText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.8' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  exerciseIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '18px' },
  partHeader: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A', background: '#F2F0EC', padding: '10px 14px', marginBottom: '12px', borderLeft: '3px solid #D4A847' },
  field: { marginBottom: '16px' },
  fieldLabel: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A', marginBottom: '6px', lineHeight: '1.5' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  scenBlock: { border: '1.5px solid #E8E6E2' },
  scenHeader: { background: '#F2F0EC', padding: '10px 14px', fontSize: '12px', fontWeight: '700', color: '#1B2A4A', borderBottom: '1px solid #E8E6E2' },
  scenText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', padding: '12px 14px', borderBottom: '1px solid #E8E6E2', fontStyle: 'italic' },
  scenTypeRow: { display: 'flex', gap: '20px', padding: '10px 14px', borderBottom: '1px solid #E8E6E2', alignItems: 'center', flexWrap: 'wrap' },
  scenField: { padding: '10px 14px' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' },
  critTable: { border: '1.5px solid #E8E6E2', marginBottom: '14px' },
  critHeader: { display: 'flex', background: '#1B2A4A' },
  critHCol: { padding: '9px 12px', fontSize: '10px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  critRow: { display: 'flex' },
  critCell: { padding: '11px 12px', fontSize: '12px', color: '#3D3D3D', lineHeight: '1.5', borderRight: '1px solid #E8E6E2' },
  critCheckCell: { padding: '10px 12px', background: '#FAF8F5', borderRight: '1px solid #E8E6E2', display: 'flex', flexDirection: 'column', gap: '6px' },
  critNotesCell: { padding: '4px' },
  critNotesInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px', boxSizing: 'border-box' },
  ingredBlock: { border: '1.5px solid #E8E6E2' },
  ingredTitle: { background: '#1B2A4A', color: '#D4A847', padding: '8px 16px', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' },
  ingredRow: { display: 'flex' },
  ingredLabel: { width: '80px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center', justifyContent: 'flex-start' },
  ingredNum: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A' },
  ingredName: { fontSize: '10px', fontWeight: '700', color: '#1B2A4A', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' },
  ingredHint: { width: '170px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2', fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.5' },
  rubricTable: { border: '1.5px solid #E8E6E2', fontSize: '12px', marginBottom: '8px' },
  rubricHeader: { display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr 1fr', background: '#1B2A4A', color: '#FFFFFF', fontWeight: '700', fontSize: '11px' },
  rubricRow: { display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E6E2' },
  rubricCol0: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', fontSize: '11px' },
  rubricCol: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', color: '#3D3D3D', lineHeight: '1.5', fontSize: '11px' },
  scoreRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  scoreField: { display: 'flex', alignItems: 'center', gap: '6px' },
  scoreLabel: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A' },
  scoreSelect: { border: '1.5px solid #E8E6E2', padding: '6px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', background: '#FAF8F5', outline: 'none', width: '56px' },
  scoreOf: { fontSize: '12px', color: '#3D3D3D' },
  scoreTotalBox: { marginLeft: 'auto', background: '#1B2A4A', padding: '8px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scoreTotalLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' },
  scoreTotalVal: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '900', color: '#D4A847' },
  aiBox: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  aiBoxTitle: { fontSize: '13px', fontWeight: '600', color: '#D4A847', marginBottom: '6px' },
  aiBoxSub: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '300', marginBottom: '12px' },
  aiBoxText: { fontSize: '12px', color: '#FFFFFF', lineHeight: '1.8', marginBottom: '12px', fontWeight: '300', whiteSpace: 'pre-line' },
  copyBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  revisionLabel: { fontSize: '12px', fontWeight: '700', color: '#1B2A4A', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '12px' },
  callout: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  calloutTitle: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  calloutBody: { fontSize: '13px', color: '#FFFFFF', fontWeight: '300', lineHeight: '1.7' },
  winsTable: { border: '1.5px solid #E8E6E2' },
  winsHeader: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#1B2A4A' },
  winsCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  winsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: '1px solid #E8E6E2' },
  winsCell: { borderRight: '1px solid #E8E6E2', padding: '4px' },
  winsInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px' },
  bottomBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  savedNote: { fontSize: '11px', color: '#1a8a4a', fontWeight: '500' },
  completeRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  completedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '8px 16px', fontSize: '12px', fontWeight: '700' },
  unmarkBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  completeBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '12px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(27,42,74,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: '#FAF8F5', padding: '36px 40px', maxWidth: '420px', width: '90%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  modalText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px' },
  modalCancel: { background: 'transparent', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  modalConfirm: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
